/****
 * all functions are sorted alphabetically
 */

import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PAGE_MAP, PAGE_ROUTE } from '@app/constants/page-route';
import { ServiceLineType } from '@app/enums/service-type.enum';
import { AccessRoleType } from '@app/modules/user-management/enums/access-role-type.enum';
import { IAppClaims } from '@app/types/app-claims';
import { environment } from '@env/environment';

@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    private router = inject(Router);

    getDefaultUrl(appClaims: IAppClaims): string {
        const { roles, supplychain } = appClaims;
        // Check if the user has supply chain first
        const supplychainList = supplychain ?? [];
        if (supplychainList.length > 0) {
            const firstAvailableModule = supplychainList.find(
                (supplyChain) => supplyChain.module,
            );
            if (firstAvailableModule) {
                const selectedModule = firstAvailableModule.module;
                switch (selectedModule) {
                    case environment.serviceLines[ServiceLineType.Testing]: {
                        return PAGE_MAP.main.testing.summary;
                    }
                    case environment.serviceLines[ServiceLineType.Inspection]: {
                        return PAGE_MAP.main.inspection.summary;
                    }
                    default: {
                        //handle the case when client is null /rejected
                        return PAGE_MAP.main.error.unauthorized;
                    }
                }
            }
        }

        const su = roles.includes(environment.accessRoles[AccessRoleType.SA]);
        if (su) {
            return PAGE_MAP.main.userManagement.superAdmin;
        }
        const am = roles.includes(environment.accessRoles[AccessRoleType.AccountManager]);
        if (am) {
            return PAGE_MAP.main.userManagement.client;
        }
        return PAGE_MAP.main.dashboard;
    }

    goToClientManagementDefaultPage(id: string) {
        this.navigateToPage(`${PAGE_MAP.main.clientManagement.default}/${id}`);
    }
    goToDashboardPage() {
        this.navigateToPage(PAGE_MAP.main.dashboard);
    }

    goToDefaultPage(appClaims: IAppClaims) {
        const defaultUrl = this.getDefaultUrl(appClaims);
        this.navigateToPage(defaultUrl);
    }

    goToETRFPage() {
        this.navigateToPage(PAGE_MAP.main.etrf.default);
    }

    goToHomePage() {
        this.navigateToPage(PAGE_MAP.main.home);
    }
    goToInspectionPage() {
        this.navigateToPage(PAGE_MAP.main.inspection.default);
    }
    goToInspectionAnalyticsPage() {
        this.navigateToPage(PAGE_MAP.main.inspection.analytics);
    }

    goToNextGenValidateUserPage() {
        this.navigateToPage(`/${PAGE_ROUTE.NextGenValidateUser}`);
    }

    goToNotFoundPage() {
        this.navigateToPage(PAGE_MAP.main.error.notFound);
    }

    goToPublicNotFoundPage() {
        this.navigateToPage(PAGE_MAP.public.error.notFound);
    }

    goToTestingSummaryPage() {
        this.navigateToPage(PAGE_MAP.main.testing.summary);
    }

    goToUnauthorizedPage() {
        this.navigateToPage(PAGE_MAP.main.error.unauthorized);
    }

    goToUserManagementClientPage() {
        this.navigateToPage(PAGE_MAP.main.userManagement.client);
    }

    private navigateToPage(url: string) {
        void this.router.navigate([url]);
    }
}
